import {
    type Dispatch,
    memo,
    type SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { CalendarOutlined } from '@ant-design/icons';
import { Button, DatePicker } from 'antd';
import dayjs from 'dayjs';

import { Card } from '@/shared/ui/card';

import { useLazyGetAccountCalendarDataQuery } from '../../api/trading-platform.api';
import { calendarDataTransformer } from '../../lib/calendar-data-transformer';
import { useGetTradingPlatformSessionId } from '../../models/selectors/get-trading-platform-session-id';
import { useGetTradingPlatformSessionType } from '../../models/selectors/get-trading-platform-session-type';
import { TradingPlatformCalendarAsDrawer } from '../trading-platform-calendar-as-drawer/trading-platform-calendar-as-drawer';
import { TradingPlatformDailyTradesCardList } from '../trading-platform-daily-trades-card-list/trading-platform-daily-trades-card-list';
import { TradingPlatformMonthSwitcher } from '../trading-platform-month-switcher/trading-platform-month-switcher';

import cls from './trading-platform-monthly-trades-card.module.css';

export interface TradingCalendarProps {
    value: dayjs.Dayjs;
    setValue: Dispatch<SetStateAction<dayjs.Dayjs>>;
}

export const TradingPlatformMonthlyTradesCard = memo(
    (props: TradingCalendarProps) => {
        const { value, setValue } = props;
        const accountId = useGetTradingPlatformSessionId();
        const connectionType = useGetTradingPlatformSessionType();

        const [
            getAccountCalendarDataQuery,
            {
                data: calendarData,
                isFetching: isGetAccountCalendarDataFetching,
            },
        ] = useLazyGetAccountCalendarDataQuery();

        const [isOpened, setIsOpened] = useState<boolean>(false);

        const dataSource = useMemo(
            () => calendarDataTransformer(calendarData),
            [calendarData],
        );

        const onPrevMonthHandler = useCallback(() => {
            setValue((prevValue) => prevValue.subtract(1, 'month'));
        }, [setValue]);

        const onNextMonthHandler = useCallback(() => {
            setValue((prevValue) => prevValue.add(1, 'month'));
        }, [setValue]);

        const onOpen = useCallback(() => {
            setIsOpened(true);
        }, []);

        const onClose = useCallback(() => {
            setIsOpened(false);
        }, []);

        const onChangeDatePicker = useCallback(
            (newValue: dayjs.Dayjs) => {
                setValue(newValue);
            },
            [setValue],
        );

        const titleContent = useMemo(
            () => (
                <TradingPlatformMonthSwitcher
                    isLoading={isGetAccountCalendarDataFetching}
                    onNextMonth={onNextMonthHandler}
                    onPrevMonth={onPrevMonthHandler}
                    value={value}
                />
            ),
            [
                value,
                onPrevMonthHandler,
                onNextMonthHandler,
                isGetAccountCalendarDataFetching,
            ],
        );

        const drawerExtra = useMemo(
            () => (
                <DatePicker
                    allowClear={false}
                    disabled={isGetAccountCalendarDataFetching}
                    onChange={onChangeDatePicker}
                    picker="month"
                    value={value}
                />
            ),
            [value, onChangeDatePicker, isGetAccountCalendarDataFetching],
        );

        useEffect(() => {
            if (accountId && connectionType) {
                getAccountCalendarDataQuery({
                    id: accountId,
                    date: value.startOf('month').format('YYYY-MM-DD'),
                    connectionType,
                });
            }
        }, [accountId, getAccountCalendarDataQuery, value, connectionType]);

        return (
            <>
                <Card
                    className={cls.root}
                    extra={
                        <Button
                            disabled={isGetAccountCalendarDataFetching}
                            icon={<CalendarOutlined />}
                            onClick={onOpen}
                            shape="circle"
                            size="large"
                        />
                    }
                    title={titleContent}
                >
                    <TradingPlatformDailyTradesCardList
                        dataSource={dataSource}
                        isLoading={isGetAccountCalendarDataFetching}
                    />
                </Card>
                <TradingPlatformCalendarAsDrawer
                    dataSource={dataSource}
                    extraSlot={drawerExtra}
                    isLoading={isGetAccountCalendarDataFetching}
                    isOpened={isOpened}
                    onClose={onClose}
                    titleSlot={titleContent}
                    value={value}
                />
            </>
        );
    },
);

TradingPlatformMonthlyTradesCard.displayName =
    'TradingPlatformMonthlyTradesCard';
